import React, { useEffect, useState } from 'react';
import { Loader2, Save, Type, Video } from 'lucide-react';
import { adminApi, formatApiErrorDetail } from './api';
import { useLanguage } from '../context/LanguageContext';

/**
 * AdminSectionCopy — Phase D screen for editing:
 *   • section_copy   — every remaining bilingual section title/subtitle/eyebrow
 *   • diver          — Diver cinematic (video + 2 title lines + subtitle)
 *
 * Each named slot lives at `settings.section_copy.<key> = {ar, en}`.
 * Blank field means "use the built-in default" — the public site never breaks.
 */

const inputCls =
  'w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue/40';

// Slot definitions grouped by page — each slot key maps to a settings.section_copy field.
const GROUPS = [
  {
    id: 'home',
    ar: 'الصفحة الرئيسية',
    en: 'Homepage',
    slots: [
      { key: 'about_title',                    ar: 'عنوان قسم "عن الشركة"',       en: 'About section title', short: true },
      { key: 'visionmission_eyebrow',          ar: 'شارة قسم الرؤية والرسالة',    en: 'Vision/Mission eyebrow', short: true },
      { key: 'visionmission_heading_left',     ar: 'عنوان الرؤية والرسالة — يسار', en: 'Vision/Mission heading (left)', short: true },
      { key: 'visionmission_heading_right',    ar: 'عنوان الرؤية والرسالة — يمين', en: 'Vision/Mission heading (right)', short: true },
      { key: 'vision_title',                   ar: 'عنوان بطاقة الرؤية',          en: 'Vision card title', short: true },
      { key: 'mission_title',                  ar: 'عنوان بطاقة الرسالة',         en: 'Mission card title', short: true },
      { key: 'stats_heading',                  ar: 'عنوان قسم الإنجازات',         en: 'Stats section heading', short: true },
      { key: 'vision2030_eyebrow',             ar: 'شارة قسم رؤية 2030',          en: 'Vision 2030 eyebrow', short: true },
      { key: 'vision2030_heading_prefix',      ar: 'بداية عنوان رؤية 2030',       en: 'Vision 2030 heading (prefix)', short: true },
      { key: 'vision2030_heading_highlight',   ar: 'الجزء المتدرج من العنوان',    en: 'Vision 2030 heading (highlight)', short: true },
      { key: 'vision2030_subtitle',            ar: 'وصف قسم رؤية 2030',           en: 'Vision 2030 subtitle', short: false },
      { key: 'vision2030_pledge_label',        ar: 'شارة "رسالتنا" (رؤية 2030)',  en: 'Vision 2030 pledge label', short: true },
      { key: 'vision2030_stat_pillars',        ar: 'تعليق "ركائز رئيسية"',        en: 'Stat caption — Pillars', short: true },
      { key: 'vision2030_stat_horizon',        ar: 'تعليق "الأفق الوطني"',        en: 'Stat caption — Horizon', short: true },
      { key: 'vision2030_stat_commitment',     ar: 'تعليق "التزام"',              en: 'Stat caption — Commitment', short: true },
      { key: 'vision2030_contribution_heading', ar: 'عنوان "مساهمتنا في الركائز"', en: 'Contribution sub-heading', short: true },
      { key: 'responsibility_eyebrow',         ar: 'شارة قسم المسؤولية',          en: 'Responsibility eyebrow', short: true },
      { key: 'responsibility_heading_prefix',  ar: 'بداية عنوان المسؤولية',       en: 'Responsibility heading (prefix)', short: true },
      { key: 'responsibility_heading_highlight', ar: 'الجزء المتدرج من العنوان',  en: 'Responsibility heading (highlight)', short: true },
      { key: 'responsibility_operating',       ar: 'شارة "مبادئنا التشغيلية"',    en: '“Operating Principles” label', short: true },
      { key: 'responsibility_pledge',          ar: 'شارة "إيماننا"',              en: '“Our Pledge” label', short: true },
      { key: 'partners_heading',               ar: 'عنوان قسم الشركاء',           en: 'Partners heading', short: true },
      { key: 'partners_subtitle',              ar: 'وصف قسم الشركاء',             en: 'Partners subtitle', short: false },
    ],
  },
  {
    id: 'services',
    ar: 'صفحة الخدمات',
    en: 'Services page',
    slots: [
      { key: 'studies_heading',   ar: 'عنوان قسم الدراسات',   en: 'Studies heading', short: true },
      { key: 'studies_subtitle',  ar: 'وصف قسم الدراسات',    en: 'Studies subtitle', short: true },
      { key: 'projects_eyebrow',  ar: 'شارة قسم المشاريع',    en: 'Projects eyebrow', short: true },
      { key: 'projects_heading',  ar: 'عنوان قسم المشاريع',   en: 'Projects heading', short: true },
      { key: 'projects_subtitle', ar: 'وصف قسم المشاريع',    en: 'Projects subtitle', short: false },
      { key: 'gallery_heading',   ar: 'عنوان قسم المعرض',     en: 'Gallery heading', short: true },
      { key: 'gallery_subtitle',  ar: 'وصف قسم المعرض',      en: 'Gallery subtitle', short: true },
    ],
  },
  {
    id: 'accreditations',
    ar: 'صفحة الاعتمادات',
    en: 'Accreditations page',
    slots: [
      { key: 'acc_section1_eyebrow',  ar: 'شارة قسم الاعتمادات',      en: 'Accreditations eyebrow', short: true },
      { key: 'acc_section1_heading',  ar: 'عنوان قسم الاعتمادات',     en: 'Accreditations heading', short: true },
      { key: 'acc_section1_body_p1',  ar: 'الفقرة الأولى',             en: 'Body paragraph 1', short: false },
      { key: 'acc_section1_body_p2',  ar: 'الفقرة الثانية',            en: 'Body paragraph 2', short: false },
      { key: 'acc_section1_caption',  ar: 'التعليق التقديمي للشعارات', en: 'Logo grid caption', short: false },
      { key: 'acc_section2_eyebrow',  ar: 'شارة قسم الكيان القانوني',  en: 'Legal Entity eyebrow', short: true },
      { key: 'acc_section2_heading',  ar: 'عنوان قسم الكيان القانوني', en: 'Legal Entity heading', short: true },
    ],
  },
];

const Field = ({ label, dir, children }) => (
  <div>
    <label
      dir={dir}
      className={`block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1.5 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
    >
      {label}
    </label>
    {children}
  </div>
);

const Pair = ({ slot, value, onChange, isAr }) => (
  <div className={`rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-800 p-3 ${isAr ? 'text-right' : 'text-left'}`}>
    <div className={`text-[11px] font-bold text-brand-blue-700 dark:text-brand-blue-300 mb-2 ${isAr ? 'text-right' : ''}`}>
      {isAr ? slot.ar : slot.en}
      <span className="ml-2 rtl:mr-2 rtl:ml-0 text-slate-400 dark:text-slate-500 font-mono text-[10px]">{slot.key}</span>
    </div>
    <div className="grid md:grid-cols-2 gap-2">
      <Field label={isAr ? 'عربي' : 'Arabic'} dir="rtl">
        {slot.short
          ? <input dir="rtl" className={inputCls} value={value.ar || ''} onChange={(e) => onChange({ ...value, ar: e.target.value })} />
          : <textarea rows={2} dir="rtl" className={inputCls} value={value.ar || ''} onChange={(e) => onChange({ ...value, ar: e.target.value })} />
        }
      </Field>
      <Field label={isAr ? 'إنجليزي' : 'English'} dir="ltr">
        {slot.short
          ? <input dir="ltr" className={inputCls} value={value.en || ''} onChange={(e) => onChange({ ...value, en: e.target.value })} />
          : <textarea rows={2} dir="ltr" className={inputCls} value={value.en || ''} onChange={(e) => onChange({ ...value, en: e.target.value })} />
        }
      </Field>
    </div>
  </div>
);

const AdminSectionCopy = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [sectionCopy, setSectionCopy] = useState({});
  const [diver, setDiver] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [sc, dv] = await Promise.all([
        adminApi.get('/settings/section_copy'),
        adminApi.get('/settings/diver'),
      ]);
      setSectionCopy((sc.data && sc.data.value) || {});
      setDiver((dv.data && dv.data.value) || {});
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const patchSlot = (key, val) => setSectionCopy((prev) => ({ ...prev, [key]: val }));
  const patchDiver = (key, val) => setDiver((prev) => ({ ...prev, [key]: val }));

  const saveOne = async (settingsKey, value) => {
    setSavingKey(settingsKey); setError(''); setMsg('');
    try {
      await adminApi.put(`/settings/${settingsKey}`, { value });
      setMsg(isAr ? 'تم الحفظ بنجاح' : 'Saved successfully');
      setTimeout(() => setMsg(''), 2200);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSavingKey(''); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-brand-blue" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-sectioncopy-page" dir={isAr ? 'rtl' : 'ltr'}>
      <div className={isAr ? 'text-right' : 'text-left'}>
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-white">
          {isAr ? 'عناوين الأقسام' : 'Section Copy'}
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          {isAr
            ? 'حرّر عناوين وأوصاف الأقسام والقسم السينمائي (Diver). اترك أي حقل فارغاً لاستخدام النص الافتراضي.'
            : 'Edit section titles, subtitles, and the Diver cinematic. Leave any field blank to use the built-in default.'}
        </p>
      </div>
      {error && <div className="px-4 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 text-sm">{error}</div>}
      {msg && <div className="px-4 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 text-sm">{msg}</div>}

      {/* ---------- SECTION COPY GROUPS ---------- */}
      {GROUPS.map((group) => (
        <section
          key={group.id}
          data-testid={`sc-group-${group.id}`}
          className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4"
        >
          <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
            <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue/15 to-brand-green/15 border border-brand-blue/25 flex items-center justify-center">
              <Type className="w-5 h-5 text-brand-blue-700 dark:text-brand-blue-300" />
            </span>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {isAr ? group.ar : group.en}
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {group.slots.length} {isAr ? 'حقل' : 'fields'}
              </p>
            </div>
          </div>
          <div className="space-y-3">
            {group.slots.map((slot) => (
              <Pair
                key={slot.key}
                slot={slot}
                value={sectionCopy[slot.key] || {}}
                onChange={(v) => patchSlot(slot.key, v)}
                isAr={isAr}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Global save for section_copy */}
      <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
        <button
          type="button"
          data-testid="sc-save"
          onClick={() => saveOne('section_copy', sectionCopy)}
          disabled={savingKey === 'section_copy'}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-brand-blue to-brand-blue-700 text-white text-sm font-semibold shadow disabled:opacity-60"
        >
          {savingKey === 'section_copy' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isAr ? 'حفظ عناوين الأقسام' : 'Save section copy'}
        </button>
      </div>

      {/* ---------- DIVER CINEMATIC ---------- */}
      <section
        data-testid="sc-diver"
        className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5 md:p-6 space-y-4"
      >
        <div className={`flex items-center gap-3 ${isAr ? 'flex-row-reverse text-right' : ''}`}>
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/15 to-blue-500/15 border border-cyan-500/25 flex items-center justify-center">
            <Video className="w-5 h-5 text-cyan-700 dark:text-cyan-300" />
          </span>
          <div className="flex-1">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white">
              {isAr ? 'القسم السينمائي (Diver)' : 'Diver Cinematic'}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {isAr ? 'الفيديو الخلفي، السطر الأول، السطر الثاني، والوصف.' : 'Background video, title line 1, title line 2, and subtitle.'}
            </p>
          </div>
        </div>

        <Field label={isAr ? 'رابط الفيديو (URL)' : 'Video URL'} dir="ltr">
          <input dir="ltr" className={inputCls} value={diver.video_url || ''} onChange={(e) => patchDiver('video_url', e.target.value)} placeholder="https://…/video.mp4" />
        </Field>

        <Pair
          slot={{ key: 'title_line1', ar: 'العنوان — السطر الأول', en: 'Title line 1', short: true }}
          value={diver.title_line1 || {}}
          onChange={(v) => patchDiver('title_line1', v)}
          isAr={isAr}
        />
        <Pair
          slot={{ key: 'title_line2', ar: 'العنوان — السطر الثاني (متدرج)', en: 'Title line 2 (gradient)', short: true }}
          value={diver.title_line2 || {}}
          onChange={(v) => patchDiver('title_line2', v)}
          isAr={isAr}
        />
        <Pair
          slot={{ key: 'subtitle', ar: 'الوصف', en: 'Subtitle', short: false }}
          value={diver.subtitle || {}}
          onChange={(v) => patchDiver('subtitle', v)}
          isAr={isAr}
        />

        <div className={`flex ${isAr ? 'justify-start' : 'justify-end'}`}>
          <button
            type="button"
            data-testid="sc-diver-save"
            onClick={() => saveOne('diver', diver)}
            disabled={savingKey === 'diver'}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold shadow disabled:opacity-60"
          >
            {savingKey === 'diver' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isAr ? 'حفظ القسم السينمائي' : 'Save Diver'}
          </button>
        </div>
      </section>
    </div>
  );
};

export default AdminSectionCopy;
